import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { Activity } from '@robocorp/robocloud-internal-cds-sdk';

import {
  ApiQueryFnData,
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  validateApiResponse,
} from '../utils';
import { activityService } from '~/services/processAPI';
import { useCurrentWorkspaceQueryKey } from '../utils/useQueryKey';
import { useLazyQuery } from '../useLazyQuery';
import { CreateActivityPayload } from '~/services/processAPI/activityService';

const ROOT_KEY = 'activities';
const getRootQueryKey = () => [ROOT_KEY];
export const getProcessActivitiesQueryKey = (processId: string) => [ROOT_KEY, processId];
const getActivityQueryKey = (activityId: string) => [ROOT_KEY, activityId];

export enum ActivityQueryErrors {
  GET_ACTIVITIES_ERROR = 'GET_ACTIVITIES_ERROR',
  GET_ACTIVITY_ERROR = 'GET_PROCESS_ACTIVITY_ERROR',
}

export const useActivitiesQuery = (
  options?: UseApiQueryOptions<typeof activityService.listWorkspaceActivities>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(getRootQueryKey()),
    async () =>
      validateApiResponse(await activityService.listWorkspaceActivities({ workspaceId })).data,
    {
      onError: (error) =>
        dispatch(
          formatErrorPayload(error, {
            // type: ActivityQueryErrors.GET_ACTIVITIES_ERROR,
          }),
        ),
      ...options,
    },
  );
};

export const useProcessActivitiesQuery = (
  processId: string,
  options?: UseApiQueryOptions<typeof activityService.getActivitiesByProcessId>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getProcessActivitiesQueryKey(processId),
    async () =>
      validateApiResponse(
        await activityService.getActivitiesByProcessId({ workspaceId, processId }),
      ).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useProcessActivitiesLazyQuery = () => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useLazyQuery<{ processId: string }, Activity[] | undefined, Error>(
    ({ processId }) => getProcessActivitiesQueryKey(processId),
    ({ processId }) =>
      async () =>
        validateApiResponse(
          await activityService.getActivitiesByProcessId({ workspaceId, processId }),
        ).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
    },
  );
};

export const useActivityQuery = (
  { processId, activityId }: { processId: string; activityId: string },
  options?: UseApiQueryOptions<typeof activityService.getActivity>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getActivityQueryKey(activityId),
    async () =>
      validateApiResponse(await activityService.getActivity({ workspaceId, processId, activityId }))
        .data,
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: ActivityQueryErrors.GET_ACTIVITY_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useCreateActivityMutation = <T extends typeof activityService.create>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, activity }: { processId: string; activity: CreateActivityPayload }) =>
      validateApiResponse(
        await activityService.create({ workspaceId, processId, payload: activity }),
      ).data,
    {
      onSuccess: (activity, { processId }) => {
        if (activity) {
          // Update Process Activities
          queryClient.setQueryData(
            getProcessActivitiesQueryKey(processId),
            (activities?: Activity[]) => (activities ? [...activities, activity] : [activity]),
          );
        }
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useEditActivityMutation = <T extends typeof activityService.editActivity>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      processId,
      activityId,
      activity,
    }: {
      processId: string;
      activityId: string;
      activity: ApiQueryFnData<T>;
    }) =>
      validateApiResponse(
        await activityService.editActivity({ workspaceId, processId, activityId, activity }),
      ).data,
    {
      onSuccess: (activity, { processId, activityId }) => {
        if (activity) {
          queryClient.setQueryData(getActivityQueryKey(activityId), activity);

          // Update Process Activities
          queryClient.setQueryData(
            getProcessActivitiesQueryKey(processId),
            (activities?: Activity[]) =>
              activities
                ? activities.map((curr) => (curr.id === activityId ? activity : curr))
                : [activity],
          );
        }
      },
      onError: (error) => dispatch(formatErrorPayload(error, { displayOriginalError: true })),
      ...options,
    },
  );
};

export const useDeleteActivityMutation = (
  options?: UseApiMutationOptions<typeof activityService.deleteActivity>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, activityId }: { processId: string; activityId: string }) =>
      validateApiResponse(
        await activityService.deleteActivity({ workspaceId, processId, activityId }),
      ).data,
    {
      onSuccess: (_, { processId, activityId }) => {
        // Update Process Activities
        queryClient.setQueryData(
          getProcessActivitiesQueryKey(processId),
          (activities?: Activity[]) =>
            activities ? activities.filter((curr) => curr.id !== activityId) : [],
        );
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};
